<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';

// --- Types ---

interface AdminStats {
  totalFits: number;
  publicFits: number;
  totalUsers: number;
  fitsToday: number;
  usersToday: number;
}

interface RecentFit {
  fitID: number;
  fitName: string;
  shipTypeID: number;
  shipName: string;
  isPublic: number;
  createdAt: string;
  character_name: string | null;
}

interface RecentUser {
  id: number;
  character_name: string;
  character_id: number;
  fit_count: number;
  last_login: string;
  created_at: string;
}

interface AuditEntry {
  id: number;
  action: string;
  target: string;
  details: string;
  ip: string;
  created_at: string;
}

// --- State ---

const isAdmin = ref(false);
const loading = ref(true);
const loginError = ref('');

const loginForm = ref({ username: '', password: '' });

const stats = ref<AdminStats | null>(null);
const recentFits = ref<RecentFit[]>([]);
const recentUsers = ref<RecentUser[]>([]);
const auditLog = ref<AuditEntry[]>([]);

const activeTab = ref<'fits' | 'users' | 'audit' | 'config'>('fits');
const actionLoading = ref<string | null>(null);

// --- Config display ---

const eveConfig = computed(() => ({
  ssoConfigured: '(check server env)',
  callbackUrl: window.location.origin + '/api/auth?action=callback',
  requiredScopes: [
    'esi-fittings.read_fittings.v1',
    'esi-fittings.write_fittings.v1',
  ],
}));

// --- API helpers ---

async function adminFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data as T;
}

// --- Auth ---

async function checkStatus(): Promise<void> {
  try {
    const data = await adminFetch<{ isAdmin: boolean }>('/api/admin?action=status');
    isAdmin.value = data.isAdmin;
  } catch {
    isAdmin.value = false;
  } finally {
    loading.value = false;
  }
}

async function doLogin(): Promise<void> {
  loginError.value = '';
  try {
    await adminFetch<{ success: boolean }>('/api/admin?action=login', {
      method: 'POST',
      body: JSON.stringify({
        action: 'login',
        username: loginForm.value.username,
        password: loginForm.value.password,
      }),
    });
    isAdmin.value = true;
    await loadDashboard();
  } catch (err) {
    loginError.value = (err as Error).message;
  }
}

async function doLogout(): Promise<void> {
  try {
    await adminFetch<{ success: boolean }>('/api/admin?action=logout');
  } finally {
    isAdmin.value = false;
    stats.value = null;
    recentFits.value = [];
    recentUsers.value = [];
    auditLog.value = [];
  }
}

// --- Dashboard data ---

async function loadDashboard(): Promise<void> {
  await Promise.all([
    loadStats(),
    loadRecentFits(),
    loadRecentUsers(),
    loadAuditLog(),
  ]);
}

async function loadStats(): Promise<void> {
  stats.value = await adminFetch<AdminStats>('/api/admin?action=stats');
}

async function loadRecentFits(): Promise<void> {
  recentFits.value = await adminFetch<RecentFit[]>('/api/admin?action=recent_fits');
}

async function loadRecentUsers(): Promise<void> {
  recentUsers.value = await adminFetch<RecentUser[]>('/api/admin?action=recent_users');
}

async function loadAuditLog(): Promise<void> {
  auditLog.value = await adminFetch<AuditEntry[]>('/api/admin?action=audit_log');
}

// --- Actions ---

async function togglePublic(fitID: number): Promise<void> {
  actionLoading.value = `toggle_${fitID}`;
  try {
    await adminFetch<{ success: boolean }>('/api/admin?action=toggle_public', {
      method: 'POST',
      body: JSON.stringify({ action: 'toggle_public', fitID }),
    });
    await loadRecentFits();
  } finally {
    actionLoading.value = null;
  }
}

async function deleteFit(fitID: number, fitName: string): Promise<void> {
  if (!confirm(`Delete fit "${fitName}" (#${fitID})? This cannot be undone.`)) return;
  actionLoading.value = `delete_fit_${fitID}`;
  try {
    await adminFetch<{ success: boolean }>('/api/admin?action=delete_fit', {
      method: 'POST',
      body: JSON.stringify({ action: 'delete_fit', fitID }),
    });
    await Promise.all([loadRecentFits(), loadStats(), loadAuditLog()]);
  } finally {
    actionLoading.value = null;
  }
}

async function deleteUser(userId: number, name: string): Promise<void> {
  if (!confirm(`Delete user "${name}" (#${userId})? This cannot be undone.`)) return;
  actionLoading.value = `delete_user_${userId}`;
  try {
    await adminFetch<{ success: boolean }>('/api/admin?action=delete_user', {
      method: 'POST',
      body: JSON.stringify({ action: 'delete_user', userId }),
    });
    await Promise.all([loadRecentUsers(), loadStats(), loadAuditLog()]);
  } finally {
    actionLoading.value = null;
  }
}

function viewFit(fitID: number): void {
  window.open(`/?fit=${fitID}`, '_blank');
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// --- Init ---

onMounted(async () => {
  await checkStatus();
  if (isAdmin.value) {
    await loadDashboard();
  }
});
</script>

<template>
  <div class="admin-page">
    <!-- Loading -->
    <div v-if="loading" class="admin-loading">
      <div class="spinner"></div>
      <p>Checking authentication...</p>
    </div>

    <!-- Login form -->
    <div v-else-if="!isAdmin" class="login-wrapper">
      <form class="login-card" @submit.prevent="doLogin">
        <h1 class="login-title">EveFit Admin</h1>
        <p class="login-subtitle">Enter your credentials to continue</p>

        <div class="form-group">
          <label for="username">Username</label>
          <input
            id="username"
            v-model="loginForm.username"
            type="text"
            autocomplete="username"
            placeholder="admin"
            required
          />
        </div>

        <div class="form-group">
          <label for="password">Password</label>
          <input
            id="password"
            v-model="loginForm.password"
            type="password"
            autocomplete="current-password"
            placeholder="Password"
            required
          />
        </div>

        <p v-if="loginError" class="login-error">{{ loginError }}</p>

        <button type="submit" class="btn btn-primary btn-full">Sign In</button>

        <a href="/" class="back-link">Back to EveFit</a>
      </form>
    </div>

    <!-- Dashboard -->
    <div v-else class="dashboard">
      <!-- Header -->
      <header class="dash-header">
        <div class="dash-header-left">
          <h1 class="dash-title">EveFit Admin</h1>
        </div>
        <div class="dash-header-right">
          <a href="/" class="btn btn-ghost">Back to Site</a>
          <button class="btn btn-ghost" @click="doLogout">Logout</button>
        </div>
      </header>

      <!-- Stat cards -->
      <section class="stat-grid">
        <div class="stat-card">
          <div class="stat-value">{{ stats?.totalFits ?? '-' }}</div>
          <div class="stat-label">Total Fits</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ stats?.publicFits ?? '-' }}</div>
          <div class="stat-label">Public Fits</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ stats?.totalUsers ?? '-' }}</div>
          <div class="stat-label">Total Users</div>
        </div>
        <div class="stat-card stat-card--accent">
          <div class="stat-value">{{ stats?.fitsToday ?? '-' }}</div>
          <div class="stat-label">Fits Today</div>
        </div>
        <div class="stat-card stat-card--accent">
          <div class="stat-value">{{ stats?.usersToday ?? '-' }}</div>
          <div class="stat-label">Users Today</div>
        </div>
      </section>

      <!-- Tab bar -->
      <nav class="tab-bar">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'fits' }"
          @click="activeTab = 'fits'"
        >Recent Fits</button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'users' }"
          @click="activeTab = 'users'"
        >Users</button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'audit' }"
          @click="activeTab = 'audit'"
        >Audit Log</button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'config' }"
          @click="activeTab = 'config'"
        >Config</button>

        <button class="tab-refresh btn btn-ghost" @click="loadDashboard">Refresh</button>
      </nav>

      <!-- Tab content -->
      <div class="tab-content">
        <!-- Recent Fits -->
        <div v-if="activeTab === 'fits'" class="table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Ship</th>
                <th>Owner</th>
                <th>Visibility</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="recentFits.length === 0">
                <td colspan="7" class="empty-row">No fits found</td>
              </tr>
              <tr v-for="fit in recentFits" :key="fit.fitID">
                <td class="cell-id">{{ fit.fitID }}</td>
                <td class="cell-name">{{ fit.fitName }}</td>
                <td>
                  <span class="ship-cell">
                    <img
                      :src="`https://images.evetech.net/types/${fit.shipTypeID}/icon?size=32`"
                      :alt="fit.shipName"
                      class="ship-icon"
                      loading="lazy"
                    />
                    {{ fit.shipName }}
                  </span>
                </td>
                <td>{{ fit.character_name || 'Anonymous' }}</td>
                <td>
                  <span
                    class="badge"
                    :class="fit.isPublic ? 'badge--green' : 'badge--dim'"
                  >{{ fit.isPublic ? 'Public' : 'Private' }}</span>
                </td>
                <td class="cell-date">{{ formatDate(fit.createdAt) }}</td>
                <td class="cell-actions">
                  <button
                    class="btn btn-xs btn-ghost"
                    title="View fit"
                    @click="viewFit(fit.fitID)"
                  >View</button>
                  <button
                    class="btn btn-xs"
                    :class="fit.isPublic ? 'btn-dim' : 'btn-green'"
                    :disabled="actionLoading === `toggle_${fit.fitID}`"
                    :title="fit.isPublic ? 'Make private' : 'Make public'"
                    @click="togglePublic(fit.fitID)"
                  >{{ fit.isPublic ? 'Unpublish' : 'Publish' }}</button>
                  <button
                    class="btn btn-xs btn-red"
                    :disabled="actionLoading === `delete_fit_${fit.fitID}`"
                    title="Delete fit"
                    @click="deleteFit(fit.fitID, fit.fitName)"
                  >Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Users -->
        <div v-if="activeTab === 'users'" class="table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Character</th>
                <th>Character ID</th>
                <th>Fits</th>
                <th>Last Login</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="recentUsers.length === 0">
                <td colspan="7" class="empty-row">No users found</td>
              </tr>
              <tr v-for="user in recentUsers" :key="user.id">
                <td class="cell-id">{{ user.id }}</td>
                <td>
                  <span class="user-cell">
                    <img
                      :src="`https://images.evetech.net/characters/${user.character_id}/portrait?size=32`"
                      :alt="user.character_name"
                      class="user-portrait"
                      loading="lazy"
                    />
                    {{ user.character_name }}
                  </span>
                </td>
                <td class="cell-mono">{{ user.character_id }}</td>
                <td>{{ user.fit_count }}</td>
                <td class="cell-date">{{ formatDate(user.last_login) }}</td>
                <td class="cell-date">{{ formatDate(user.created_at) }}</td>
                <td class="cell-actions">
                  <button
                    class="btn btn-xs btn-red"
                    :disabled="actionLoading === `delete_user_${user.id}`"
                    title="Delete user"
                    @click="deleteUser(user.id, user.character_name)"
                  >Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Audit Log -->
        <div v-if="activeTab === 'audit'" class="table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>Target</th>
                <th>Details</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="auditLog.length === 0">
                <td colspan="5" class="empty-row">No audit entries</td>
              </tr>
              <tr v-for="entry in auditLog" :key="entry.id">
                <td class="cell-date">{{ formatDate(entry.created_at) }}</td>
                <td>
                  <span
                    class="badge"
                    :class="{
                      'badge--red': entry.action.includes('delete'),
                      'badge--green': entry.action === 'login',
                      'badge--orange': entry.action === 'login_failed',
                      'badge--blue': entry.action === 'toggle_public',
                    }"
                  >{{ entry.action }}</span>
                </td>
                <td class="cell-mono">{{ entry.target }}</td>
                <td>{{ entry.details }}</td>
                <td class="cell-mono">{{ entry.ip }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Config -->
        <div v-if="activeTab === 'config'" class="config-panel">
          <h3>EVE SSO Configuration</h3>
          <div class="config-grid">
            <div class="config-row">
              <span class="config-label">SSO Status</span>
              <span class="config-value">{{ eveConfig.ssoConfigured }}</span>
            </div>
            <div class="config-row">
              <span class="config-label">Callback URL</span>
              <span class="config-value cell-mono">{{ eveConfig.callbackUrl }}</span>
            </div>
            <div class="config-row">
              <span class="config-label">Required Scopes</span>
              <span class="config-value">
                <code v-for="scope in eveConfig.requiredScopes" :key="scope" class="scope-tag">
                  {{ scope }}
                </code>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* --- Variables (match site EVE theme) --- */
.admin-page {
  --bg-dark: #1a1a2e;
  --bg-darker: #0f0f1a;
  --bg-panel: #16213e;
  --bg-hover: #1f3460;
  --border-color: #2a3f5f;
  --text-primary: #e8e8e8;
  --text-secondary: #8892a0;
  --accent-blue: #4a9eff;
  --accent-green: #4caf50;
  --accent-red: #f44336;
  --accent-orange: #ff9800;

  min-height: 100vh;
  background: var(--bg-darker);
  color: var(--text-primary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.5;
}

/* --- Loading --- */
.admin-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 16px;
  color: var(--text-secondary);
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color);
  border-top-color: var(--accent-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* --- Login --- */
.login-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
}

.login-card {
  background: var(--bg-dark);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 40px 36px 32px;
  width: 100%;
  max-width: 380px;
}

.login-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--accent-blue);
  margin: 0 0 4px;
  text-align: center;
}

.login-subtitle {
  font-size: 0.85rem;
  color: var(--text-secondary);
  text-align: center;
  margin: 0 0 28px;
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.form-group input {
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-darker);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.form-group input:focus {
  border-color: var(--accent-blue);
}

.form-group input::placeholder {
  color: var(--text-secondary);
  opacity: 0.5;
}

.login-error {
  color: var(--accent-red);
  font-size: 0.82rem;
  margin: 0 0 12px;
  text-align: center;
}

.back-link {
  display: block;
  text-align: center;
  margin-top: 16px;
  font-size: 0.82rem;
  color: var(--text-secondary);
  text-decoration: none;
}

.back-link:hover {
  color: var(--accent-blue);
}

/* --- Buttons --- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-panel);
  color: var(--text-primary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s;
  text-decoration: none;
  white-space: nowrap;
}

.btn:hover { background: var(--bg-hover); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-primary {
  background: var(--accent-blue);
  border-color: var(--accent-blue);
  color: #fff;
  font-weight: 600;
}

.btn-primary:hover {
  background: #3a8ae8;
}

.btn-full { width: 100%; }

.btn-ghost {
  background: transparent;
  border-color: transparent;
  color: var(--text-secondary);
}

.btn-ghost:hover {
  color: var(--text-primary);
  background: var(--bg-hover);
}

.btn-xs {
  padding: 3px 8px;
  font-size: 0.75rem;
}

.btn-red {
  border-color: var(--accent-red);
  color: var(--accent-red);
  background: rgba(244, 67, 54, 0.08);
}

.btn-red:hover { background: rgba(244, 67, 54, 0.18); }

.btn-green {
  border-color: var(--accent-green);
  color: var(--accent-green);
  background: rgba(76, 175, 80, 0.08);
}

.btn-green:hover { background: rgba(76, 175, 80, 0.18); }

.btn-dim {
  border-color: var(--border-color);
  color: var(--text-secondary);
}

.btn-dim:hover { color: var(--text-primary); }

/* --- Dashboard header --- */
.dashboard {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px 40px;
}

.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 24px;
}

.dash-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--accent-blue);
  margin: 0;
}

.dash-header-right {
  display: flex;
  gap: 8px;
}

/* --- Stat cards --- */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--bg-dark);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 20px 16px;
  text-align: center;
}

.stat-card--accent {
  border-color: rgba(74, 158, 255, 0.25);
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  margin-bottom: 6px;
}

.stat-card--accent .stat-value {
  color: var(--accent-blue);
}

.stat-label {
  font-size: 0.78rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* --- Tab bar --- */
.tab-bar {
  display: flex;
  align-items: center;
  gap: 2px;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 0;
}

.tab-btn {
  padding: 10px 20px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s;
}

.tab-btn:hover {
  color: var(--text-primary);
}

.tab-btn.active {
  color: var(--accent-blue);
  border-bottom-color: var(--accent-blue);
}

.tab-refresh {
  margin-left: auto;
}

/* --- Tab content --- */
.tab-content {
  background: var(--bg-dark);
  border: 1px solid var(--border-color);
  border-top: none;
  border-radius: 0 0 6px 6px;
  min-height: 300px;
}

/* --- Tables --- */
.table-wrap {
  overflow-x: auto;
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
}

.admin-table th {
  text-align: left;
  padding: 10px 12px;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  background: var(--bg-panel);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.admin-table td {
  padding: 8px 12px;
  border-bottom: 1px solid rgba(42, 63, 95, 0.4);
  vertical-align: middle;
}

.admin-table tbody tr:hover {
  background: var(--bg-hover);
}

.empty-row {
  text-align: center;
  color: var(--text-secondary);
  padding: 32px 12px !important;
  font-style: italic;
}

.cell-id {
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}

.cell-name {
  font-weight: 500;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-date {
  color: var(--text-secondary);
  font-size: 0.78rem;
  white-space: nowrap;
}

.cell-mono {
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  font-size: 0.78rem;
  color: var(--text-secondary);
}

.cell-actions {
  display: flex;
  gap: 4px;
  white-space: nowrap;
}

/* --- Ship / user cells --- */
.ship-cell,
.user-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ship-icon {
  width: 24px;
  height: 24px;
  border-radius: 2px;
  background: var(--bg-darker);
}

.user-portrait {
  width: 24px;
  height: 24px;
  border-radius: 3px;
  background: var(--bg-darker);
}

/* --- Badges --- */
.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.badge--green {
  color: var(--accent-green);
  background: rgba(76, 175, 80, 0.12);
}

.badge--red {
  color: var(--accent-red);
  background: rgba(244, 67, 54, 0.12);
}

.badge--blue {
  color: var(--accent-blue);
  background: rgba(74, 158, 255, 0.12);
}

.badge--orange {
  color: var(--accent-orange);
  background: rgba(255, 152, 0, 0.12);
}

.badge--dim {
  color: var(--text-secondary);
  background: rgba(136, 146, 160, 0.1);
}

/* --- Config panel --- */
.config-panel {
  padding: 24px;
}

.config-panel h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 16px;
  color: var(--text-primary);
}

.config-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.config-row {
  display: flex;
  gap: 16px;
  padding: 10px 14px;
  background: var(--bg-panel);
  border-radius: 4px;
  align-items: flex-start;
}

.config-label {
  flex-shrink: 0;
  width: 140px;
  font-size: 0.8rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding-top: 2px;
}

.config-value {
  flex: 1;
  font-size: 0.85rem;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.scope-tag {
  display: inline-block;
  padding: 3px 8px;
  background: var(--bg-darker);
  border: 1px solid var(--border-color);
  border-radius: 3px;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  font-size: 0.78rem;
  color: var(--accent-blue);
}

/* --- Responsive --- */
@media (max-width: 768px) {
  .dashboard {
    padding: 0 12px 24px;
  }

  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .tab-btn {
    padding: 8px 12px;
    font-size: 0.78rem;
  }

  .admin-table {
    font-size: 0.78rem;
  }

  .config-row {
    flex-direction: column;
    gap: 4px;
  }

  .config-label {
    width: auto;
  }
}
</style>
